"use client";

import { useEffect, useRef, useState } from "react";

const ACTIVITY_EVENTS = ["pointermove", "pointerdown", "keydown", "scroll", "touchstart"] as const;

/** Returns true when the tab is hidden or the user has been idle for at least `idleMs`. */
export function useIsInactiveFor(idleMs: number): boolean {
  const [isInactive, setIsInactive] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clear = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const reset = () => {
      clear();
      setIsInactive(false);
      timerRef.current = setTimeout(() => setIsInactive(true), idleMs);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clear();
        setIsInactive(true);
      } else {
        reset();
      }
    };

    if (document.visibilityState === "hidden") {
      setIsInactive(true);
    } else {
      reset();
    }

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, reset, { passive: true });
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clear();
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, reset);
      }
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [idleMs]);

  return isInactive;
}
